import React, { useState } from "react";
import styled from "styled-components";
import GameTimer from "./GameTimer";
import { dictionaries } from "../../utils/dictionary";
import { ReactComponent as NoMicIcon } from "../../assets/images/no_mic.svg";

export type PhaseHeaderActionVariant = "primary" | "secondary" | "ghost";

export interface PhaseHeaderAction {
  id: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: PhaseHeaderActionVariant;
}

interface PhaseHeaderProps {
  title: string;
  subtitle?: string;
  meta?: React.ReactNode;
  variant?: "day" | "night";
  language: number;
  streamError?: boolean;
  onTimeoutToggle: () => void;
  onTechnicalBreak?: () => void;
  onTechnicalBreakEnd?: () => void;
  actions?: PhaseHeaderAction[];
  hideTimer?: boolean;
  // Timer state
  timerAccumulated?: number;
  timerStartedAt?: number;
  onTimerStateChange?: (accumulated: number, startedAt: number | undefined) => void;
}

const HeaderShell = styled.header<{ $variant: "day" | "night" }>`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.75rem 2.25rem;
  margin: -1.5rem -2rem 2rem;
  border-radius: 0 0 32px 32px;
  color: #ffffff;
  background: ${(props) =>
    props.$variant === "night"
      ? "linear-gradient(135deg, #0f0a1d 0%, #221b3f 45%, #36255d 100%)"
      : "linear-gradient(135deg, #281c48 0%, #3c2d6b 45%, #5b3fd5 100%)"};
  box-shadow: ${(props) =>
    props.$variant === "night"
      ? "0 24px 60px rgba(23, 10, 45, 0.55)"
      : "0 24px 60px rgba(44, 29, 96, 0.45)"};

  @media (max-width: 992px) {
    margin: -1rem -1.5rem 1.5rem;
    padding: 1.5rem 1.75rem;
    border-radius: 0 0 24px 24px;
  }

  @media (max-width: 768px) {
    margin: 0 -0.75rem 0.5rem;
    padding: 0.5rem 0.75rem 0.65rem;
    border-radius: 0 0 16px 16px;
    gap: 0.5rem;
    flex-direction: row;
    align-items: center;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 220px;
  flex: 1 1 240px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const PhaseTitle = styled.h1`
  margin: 0;
  font-size: 1.875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #ffffff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    display: none;
  }
`;

const Subtitle = styled.span`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    display: none;
  }
`;

const TimerSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    gap: 0.5rem;
  }
`;

const ActionsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex: 1 1 260px;

  @media (max-width: 768px) {
    /* flex: 0 0 auto; */
    gap: 0.4rem;
    flex-wrap: nowrap;
  }
`;

const HeaderButton = styled.button<{ $variant: PhaseHeaderActionVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 16px;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  cursor: pointer;
  border: none;
  color: ${(props) =>
    props.$variant === "ghost" ? "rgba(255, 255, 255, 0.85)" : "#ffffff"};
  background: ${(props) => {
    if (props.$variant === "primary") {
      return "linear-gradient(135deg, #7c5dff 0%, #a56dff 100%)";
    }
    if (props.$variant === "secondary") {
      return "rgba(255, 255, 255, 0.08)";
    }
    return "transparent";
  }};
  border: ${(props) =>
    props.$variant === "secondary" || props.$variant === "ghost"
      ? "1px solid rgba(255, 255, 255, 0.28)"
      : "none"};
  box-shadow: ${(props) =>
    props.$variant === "primary"
      ? "0 12px 30px rgba(124, 93, 255, 0.35)"
      : "none"};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.$variant === "primary"
        ? "0 16px 36px rgba(124, 93, 255, 0.45)"
        : "0 8px 24px rgba(0, 0, 0, 0.15)"};
    background: ${(props) => {
      if (props.$variant === "primary") {
        return "linear-gradient(135deg, #8d6bff 0%, #b67bff 100%)";
      }
      if (props.$variant === "secondary") {
        return "rgba(255, 255, 255, 0.16)";
      }
      return "rgba(255, 255, 255, 0.12)";
    }};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    flex: 0 0 auto;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 10px;
    min-width: 70px;
    white-space: nowrap;
  }
`;

const TechnicalBreakButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.16);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
    fill: #ffffff;
  }

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.24);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
`;

const ModalCard = styled.div`
  position: relative;
  width: 90%;
  max-width: 480px;
  padding: 2rem;
  border-radius: 24px;
  background: linear-gradient(135deg, #1a0f2e 0%, #2a1f45 100%);
  border: 1px solid rgba(124, 93, 255, 0.3);
  box-shadow: 0 24px 60px rgba(15, 10, 31, 0.8);
  color: #ffffff;

  @media (max-width: 768px) {
    width: 95%;
    padding: 1.5rem;
    border-radius: 20px;
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ModalMessage = styled.p`
  margin: 0 0 1.5rem;
  font-size: 1.1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ModalButton = styled.button`
  width: 100%;
  padding: 1rem;
  border-radius: 16px;
  border: none;
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #7c5dff 0%, #a56dff 100%);
  box-shadow: 0 12px 30px rgba(124, 93, 255, 0.35);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(124, 93, 255, 0.45);
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 1rem;
  }
`;

const StreamErrorBanner = styled.div<{ $show: boolean }>`
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  padding: 0.4rem 1rem;
  background: linear-gradient(135deg, #ff4d6d 0%, #ff6b88 100%);
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  border-radius: 0 0 32px 32px;
  box-shadow: 0 4px 12px rgba(255, 77, 109, 0.3);
  transform: ${({ $show }) => ($show ? 'translateY(0)' : 'translateY(-100%)')};
  opacity: ${({ $show }) => ($show ? '1' : '0')};
  transition: transform 0.3s ease, opacity 0.3s ease;
  z-index: -1;
  
  @media (max-width: 992px) {
    border-radius: 0 0 24px 24px;
    font-size: 0.75rem;
    padding: 0.35rem 0.75rem;
  }

  @media (max-width: 768px) {
    bottom: -40px;
    border-radius: 0 0 16px 16px;
    font-size: 0.7rem;
    padding: 0.3rem 0.5rem;
  }
`;

const PhaseHeader: React.FC<PhaseHeaderProps> = ({
  title,
  subtitle,
  meta,
  variant = "day",
  language,
  streamError = false,
  onTimeoutToggle,
  onTechnicalBreak,
  onTechnicalBreakEnd,
  actions = [],
  hideTimer = false,
  timerAccumulated,
  timerStartedAt,
  onTimerStateChange,
}) => {
  const [showTechnicalBreakModal, setShowTechnicalBreakModal] = useState(false);
  const dict = (dictionaries[language] || dictionaries[0]).ph;

  const handleTechnicalBreak = () => {
    setShowTechnicalBreakModal(true);
    if (onTechnicalBreak) {
      onTechnicalBreak();
    }
  };

  const handleCloseTechnicalBreak = () => {
    setShowTechnicalBreakModal(false);
    if (onTechnicalBreakEnd) {
      onTechnicalBreakEnd();
    }
  };

  return (
    <>
      <HeaderShell $variant={variant} style={{ position: 'sticky' }}>
        <TitleSection>
          <TitleRow>
            <PhaseTitle>{title}</PhaseTitle>
            {meta && <MetaPill>{meta}</MetaPill>}
          </TitleRow>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </TitleSection>

        {!hideTimer && (
          <TimerSlot>
            {onTechnicalBreak && variant === "day" && (
              <TechnicalBreakButton
                onClick={handleTechnicalBreak}
                aria-label={dict.technical_break}
              >
                <NoMicIcon />
              </TechnicalBreakButton>
            )}
            <GameTimer
              onTimeoutToggle={onTimeoutToggle}
              language={language}
              variant={variant}
              accumulated={timerAccumulated}
              startedAt={timerStartedAt}
              onTimerStateChange={onTimerStateChange}
            />
          </TimerSlot>
        )}

        {actions.length > 0 && (
          <ActionsBar>
            {actions.map((action) => (
              <HeaderButton
                key={action.id}
                onClick={action.onClick}
                disabled={action.disabled}
                $variant={action.variant ?? "secondary"}
              >
                {action.label}
              </HeaderButton>
            ))}
          </ActionsBar>
        )}
        
        <StreamErrorBanner $show={streamError}>
          ⚠️ {dict.stream_connection_error || "Network error: Check your internet connection. Stream may not be updating."}
        </StreamErrorBanner>
      </HeaderShell>

      {showTechnicalBreakModal && (
        <ModalOverlay onClick={handleCloseTechnicalBreak}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{dict.technical_break_title}</ModalTitle>
            <ModalMessage>{dict.technical_break_message}</ModalMessage>
            <ModalButton onClick={handleCloseTechnicalBreak}>
              {dict.close}
            </ModalButton>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default PhaseHeader;
